/**
 * OrderFilter
 * --
 */
import React from 'react';
import { ExpandableContainer, Content } from 'components';
import { DatePicker, Input, Select, Radio, Button } from 'antd';
import dayjs from 'dayjs';
import { useGetShopInformations } from 'hooks/ShopInformationHooks';

/**----------------------------------------
 * [Component] 필터
 * ----------------------------------------
 */
const OrderFilter = ({
  searchFilter,
  handleChange,
  getOrders,
  onResetFilter,
  defaultShop = null,
  onSelect,
  permission,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== Data ===== */
  const { data: Shop } = useGetShopInformations(null, {
    initialData: [],
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];

      const filtered = data.filter(
        (shop) => shop.type === 'ALL' || shop.type === 'SHOP',
      );

      return filtered;
    },
  });

  /* ===== STATE ===== */
  const [currentShop, setCurrentShop] = React.useState(defaultShop);

  /* ===== VARIABLE ===== */
  const formItem = [
    ...(permission === 'ADMIN'
      ? [
          {
            label: '업체',
            form: [
              {
                input: (
                  <Select
                    size="large"
                    value={currentShop}
                    onChange={(e) => setCurrentShop(e)}
                    options={[
                      {
                        shop_information_name: '전체',
                        shop_information_id: null,
                      },
                      ...Shop,
                    ].map((_item) => ({
                      label: _item.shop_information_name,
                      value: _item.shop_information_id,
                    }))}
                    style={{ ...styles.form.input.xLarge }}
                    showSearch
                    optionFilterProp="label"
                  />
                ),
              },
            ],
          },
        ]
      : []),
    {
      label: '검색어',
      form: [
        {
          input: (
            <Select
              size="large"
              value={searchFilter.keywordType}
              onChange={(e) => handleChange('keywordType', e)}
              options={[
                { label: '주문번호', value: 'shop_order_id' },
                { label: '상품명', value: 'product_name' },
                // { label: '브랜드', value: 'shop_information_name' },
                { label: '구매자', value: 'user_name' },
                { label: '연락처', value: 'user_phone' },
              ]}
              style={{ ...styles.form.input.large }}
            />
          ),
        },
        {
          input: (
            <Input
              size="large"
              value={searchFilter.keyword}
              onChange={(e) => handleChange('keyword', e.target.value)}
              style={{ ...styles.form.input.large, width: 205 }}
              placeholder="검색어를 입력해주세요"
              allowClear
            />
          ),
        },
      ],
    },
    {
      label: '기간',
      form: [
        {
          input: (
            <>
              <Radio.Group
                size="large"
                // defaultValue="today"
                buttonStyle="solid"
                value={searchFilter.dateType}
                onChange={(e) => handleChange('dateType', e.target.value)}
                style={{ marginRight: 12 }}
                disabled={
                  searchFilter.startDate || searchFilter.endDate ? true : false
                }
              >
                <Radio.Button value="today">오늘</Radio.Button>
                <Radio.Button value="week">1주일</Radio.Button>
                <Radio.Button value="month">1개월</Radio.Button>
                <Radio.Button value="3month">3개월</Radio.Button>
              </Radio.Group>
            </>
          ),
        },
        {
          input: (
            <>
              <DatePicker
                size="large"
                format={'YYYY-MM-DD'}
                placeholder="시작일"
                style={{ ...styles.form.date }}
                value={
                  searchFilter?.startDate && dayjs.unix(searchFilter?.startDate)
                }
                maxDate={
                  searchFilter?.endDate && dayjs.unix(searchFilter?.endDate)
                }
                onChange={(d) =>
                  handleChange('startDate', dayjs(d).startOf('day').unix())
                }
              />
              <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
              <DatePicker
                size="large"
                format={'YYYY-MM-DD'}
                placeholder="종료일"
                style={{ ...styles.form.date }}
                value={
                  searchFilter?.endDate && dayjs.unix(searchFilter?.endDate)
                }
                minDate={
                  searchFilter?.startDate && dayjs.unix(searchFilter?.startDate)
                }
                onChange={(d) =>
                  handleChange('endDate', dayjs(d).endOf('day').unix())
                }
              />
            </>
          ),
        },
      ],
    },

    {
      label: '결제상태',
      form: [
        {
          input: (
            <>
              <Radio.Group
                size="large"
                buttonStyle="solid"
                value={searchFilter.payment_state ?? null}
                onChange={(e) => handleChange('payment_state', e.target.value)}
                style={{ marginRight: 12 }}
              >
                <Radio value={null}>전체</Radio>
                <Radio value={'PENDING'}>결제대기</Radio>
                <Radio value={'COMPLETED'}>결제완료</Radio>
                <Radio value={'CANCELLED'}>결제취소</Radio>
                <Radio value={'PARTIALLY_REFUNDED'}>환불</Radio>
                <Radio value={'FAILED'}>결제실패</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
    {
      label: '결제수단',
      form: [
        {
          input: (
            <>
              <Radio.Group
                size="large"
                buttonStyle="solid"
                value={searchFilter.payment_method ?? null}
                onChange={(e) => handleChange('payment_method', e.target.value)}
                style={{ marginRight: 12 }}
              >
                <Radio value={null}>전체</Radio>
                <Radio value={'CARD'}>신용카드</Radio>
                <Radio value={'NAVER_PAY'}>네이버페이</Radio>
                <Radio value={'KAKAO_PAY'}>카카오페이</Radio>
                <Radio value={'JM_PAY'}>JM 페이</Radio>
                <Radio value={'TRANSFER'}>계좌이체</Radio>
                <Radio value={'MOBILE'}>휴대폰</Radio>
                <Radio value={'VIRTUAL_ACCOUNT'}>가상계좌</Radio>
                {/* <Radio value={'DIRECT_DEPOSIT'}>무통장 입금</Radio> */}
                <Radio value={'PRESENT'}>선물</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
    {
      label: '상품유형',
      form: [
        {
          input: (
            <>
              <Radio.Group
                size="large"
                buttonStyle="solid"
                value={searchFilter.type ?? null}
                onChange={(e) => handleChange('type', e.target.value)}
                style={{ marginRight: 12 }}
              >
                <Radio value={null}>전체</Radio>
                <Radio value={'DELIVERY'}>배송상품</Radio>
                {/* <Radio value={'SUBSCRIPTION'}>정기배송</Radio> */}
                <Radio value={'STORE_PICKUP'}>스토어픽업</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
  ];

  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  React.useEffect(() => {
    if (onSelect) {
      onSelect(currentShop);
    }
  }, [currentShop, onSelect]);

  /* ===== RENDER ===== */
  return (
    <>
      <Content maxWidth={'100%'} padding={0} backgroundColor={'none'}>
        <ExpandableContainer
          borderRadius={4}
          padding={20}
          className="boxShadow"
        >
          {formItem.map((_item, _index) => (
            <div
              key={`form_${_index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px 0px',
              }}
            >
              <div
                style={{
                  margin: '0 30px 0 10px',
                  fontSize: 14.5,
                  width: 60,
                  fontWeight: '500',
                  color: '#515151',
                }}
              >
                <label>{_item.label}</label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {_item.form.map((_subitem, _subkey) => (
                  <React.Fragment key={`input_${_subkey}`}>
                    {_subitem.input}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </ExpandableContainer>

        {/* 검색/초기화 버튼 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            margin: '15px 0 45px',
          }}
        >
          <Button
            size="large"
            type="primary"
            style={{
              ...styles.button,
              background: 'var(--primary-color)',
            }}
            onClick={() => getOrders()}
          >
            검색
          </Button>
          <Button
            size="large"
            style={{ ...styles.button }}
            onClick={() => {
              onResetFilter();
              setCurrentShop(defaultShop);
            }}
          >
            초기화
          </Button>
        </div>
      </Content>
    </>
  );
};

const styles = {
  title: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 20,
  },
  button: {
    width: 80,
    height: 40,
    fontSize: 15,
    fontWeight: 600,
    margin: '0px 4px',
  },
  form: {
    input: {
      xLarge: {
        width: 300,
      },
      large: {
        width: 135,
        marginRight: 8,
      },
      medium: {
        width: 100,
        marginRight: 8,
      },
    },
    button: {
      marginRight: 8,
    },
    date: {
      width: 145,
    },
    text: {
      fontSize: 13,
      fontWeight: 400,
    },
  },
};

export default OrderFilter;
